/*
 * Between "the server made the conversation" and "the sidebar has it" there is one round trip: the
 * membership event that puts a new row into the client model arrives over the socket, not in the
 * answer to the request. Navigating to the new address before it lands shows "this conversation
 * does not exist any more", which is the wrong sentence and a bad first minute.
 *
 * So a dialog waits for the model to catch up, with a deadline: a socket that is not delivering must
 * not leave a person looking at a spinner, and the address is right either way.
 *
 * Going there is the other half. In-place navigation belongs to the router and is installed by the
 * module's own screens while one of them is mounted (navigation.ts); a dialog in the sidebar can be
 * open on any screen at all, including the ones where no chat route has ever been mounted, so it
 * falls back to a full page load rather than to nothing happening.
 */

import { goBackOr } from "../../../core/shell/index.ts"
import type { ConversationId } from "../../../core/store/model.ts"
import { client } from "../../../core/store/store.ts"
import { navigateTo } from "../navigation.ts"

/** How long a dialog waits for the membership event before it navigates anyway. */
const ARRIVAL_BUDGET_MS = 5_000

/**
 * Resolves once the conversation is in the client model, or when the budget runs out — `true` and
 * `false` respectively, so a caller can tell "it is there" from "go anyway".
 */
export function whenConversationArrives(
	conv: ConversationId,
	budgetMs = ARRIVAL_BUDGET_MS,
): Promise<boolean> {
	const store = client.api
	if (store.getState().conversations[conv] !== undefined) return Promise.resolve(true)
	return new Promise((resolve) => {
		const timer = setTimeout(() => {
			unsubscribe()
			resolve(false)
		}, budgetMs)
		const unsubscribe = store.subscribe((model) => {
			if (model.conversations[conv] === undefined) return
			clearTimeout(timer)
			unsubscribe()
			resolve(true)
		})
	})
}

/** Goes to a conversation: in place while a chat screen is mounted, else a full page load. */
export function goTo(path: string): void {
	if (!navigateTo(path)) window.location.assign(path)
}

/**
 * The way out of a conversation on a window that shows one thing at a time (VISUAL.md §7.3, D-10 D9):
 * back where there is something to go back to, the list of conversations otherwise. The rule itself
 * is core's, because an AI chat's band needs the same one.
 */
export function goBackToList(): void {
	goBackOr("/")
}
